import React from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {DataGridPremium} from "@mui/x-data-grid-premium";
import type {PtTermMapping} from "../paymentTermsDemoData";
import {PT_KPIS, PT_POLICY} from "../paymentTermsDemoData";
import {formatCompactCurrency, formatPct} from "../paymentTermsUtils";
import {usePaymentTermsBucketsQuery, usePaymentTermsMappingQuery} from "../usePaymentTermsQueries";

/**
 * Where does the spend sit on the payment-term ladder, and how clean is the
 * term master data? Buckets cover all supplier parents.
 */
export const TermLandscapeView: React.FC = () => {
    const {data: buckets = [], isPending: bucketsLoading} = usePaymentTermsBucketsQuery();
    const {data: mapping = [], isPending: mappingLoading} = usePaymentTermsMappingQuery();
    const maxSpend = Math.max(0, ...buckets.map(b => b.spend));
    const currency = PT_KPIS.currency;

    const columns = React.useMemo<GridColDef<PtTermMapping>[]>(() => [
        {field: 'term', headerName: 'Payment term (ERP text)', flex: 2, minWidth: 260},
        {
            field: 'days', headerName: 'Net days', type: 'number', flex: 0.55, minWidth: 95,
            valueFormatter: (value: number, row) => row.rule.startsWith('eom') ? `EOM +${row.eomMonths}m` : `${value}d`,
        },
        {
            field: 'discountDays', headerName: 'Discount', flex: 0.7, minWidth: 125,
            valueGetter: (_value, row) => row.discountPct > 0
                ? `${(row.discountPct * 100).toFixed(1)}% within ${row.discountDays}d`
                : '—',
        },
        {
            field: 'suppliersUsing', headerName: 'Suppliers using', type: 'number', flex: 0.65, minWidth: 120,
            valueFormatter: (value: number) => value.toLocaleString('en-US'),
        },
        {
            field: 'estSpend',
            headerName: 'Est. spend (12m)',
            description: 'Supplier spend allocated equally across each supplier\'s distinct terms',
            type: 'number', flex: 0.75, minWidth: 135,
            valueFormatter: (value: number) => value > 0 ? formatCompactCurrency(value, currency) : '—',
        },
        {
            field: 'approved', headerName: 'Standard term', flex: 0.65, minWidth: 125,
            type: 'singleSelect',
            valueOptions: [{value: true, label: 'Approved'}, {value: false, label: 'Non-standard'}],
            renderCell: ({value}) => value
                ? <span className="pt-std-chip pt-std-yes">Approved</span>
                : <span className="pt-std-chip pt-std-no">Non-standard</span>,
        },
    ], [currency]);

    return <Box className="pt-landscape">
        <Box className="pt-card">
            <Box className="pt-card-header">
                <Typography variant="h2" className="pt-card-title">
                    Where the spend sits on the term ladder
                </Typography>
                <span className="pt-card-hint">
                    All {PT_KPIS.supplierCount.toLocaleString('en-US')} supplier parents · policy target {PT_POLICY.targetTermDays} days
                </span>
            </Box>
            {bucketsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28}/>
                </Box>
            ) : (
                <Box className="pt-buckets">
                    {buckets.map(b => (
                        <Box key={b.key} className="pt-bucket-row">
                            <span className={`pt-bucket-label ${b.atOrAboveTarget ? 'ok' : ''}`}>{b.label}</span>
                            <Box className="pt-bucket-bar">
                                <Box
                                    className={`pt-bucket-bar-fill ${b.atOrAboveTarget ? 'ok' : ''}`}
                                    style={{width: `${maxSpend > 0 ? (b.spend / maxSpend) * 100 : 0}%`}}
                                />
                            </Box>
                            <span className="pt-bucket-spend">{formatCompactCurrency(b.spend, currency)}</span>
                            <span className="pt-bucket-detail">
                                {b.suppliers.toLocaleString('en-US')} suppliers
                                {b.wcOpportunity > 0 && <> · <b>{formatCompactCurrency(b.wcOpportunity, currency)}</b> opportunity</>}
                            </span>
                        </Box>
                    ))}
                </Box>
            )}
            <Typography className="pt-bucket-footnote">
                {formatPct(PT_KPIS.spendBelowTargetPct)} of spend sits below the {PT_POLICY.targetTermDays}-day target —
                extending those terms to policy releases {formatCompactCurrency(PT_KPIS.wcOpportunity, currency)} of working capital.
                Buckets above target hold no extension opportunity and act as the benchmark.
            </Typography>
        </Box>

        <Box className="pt-card">
            <Box className="pt-card-header">
                <Typography variant="h2" className="pt-card-title">
                    Term repository ({mapping.length} variants)
                </Typography>
                <span className="pt-card-hint">
                    Raw ERP payment-term texts normalized to net days · approved standards: {PT_POLICY.approvedTerms.join(' / ')} days
                </span>
            </Box>
            <Box className="pt-nonstd-callout">
                ≈ <b>{formatCompactCurrency(PT_KPIS.nonStandardTermSpend, currency)}</b> of spend
                ({formatPct(PT_KPIS.nonStandardTermSpend / PT_KPIS.totalPositiveSpend)}) sits on terms that are not part of
                the approved standards ({PT_POLICY.approvedTerms.join(' / ')} days) — the first harmonisation candidate.
            </Box>
            <DataGridPremium
                className="base-table-muix"
                rows={mapping}
                columns={columns}
                loading={mappingLoading}
                getRowId={row => row.term}
                density="compact"
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[50, 100, 200]}
                initialState={{
                    pagination: {paginationModel: {pageSize: 50, page: 0}},
                    sorting: {sortModel: [{field: 'estSpend', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700},
                }}
            />
        </Box>
    </Box>;
};
